import { useCallback, useRef, useState } from 'react';
import { useActivationTick } from './ActivationContext';
import { useMutationTick } from './mutationBus';
import { fetchPlan, type FetchPlan } from './fetchPlan';

// The one SWR trigger core every data hook composes (DRY). Each hook keeps its
// own result shape (paged totals, optimistic patches, hold-until-ready windows)
// but the trigger/plan/guard skeleton — WHY this fetch is running, whether it
// bypasses the browser cache, whether to show a loading state, and whether a
// response is still current when it lands — lives here, once. Nine hooks used
// to hand-roll it; three had drifted (`nonce > 0 ? { cache: 'reload' } : {}`
// bypassed the cache on every fetch forever after one reload, and never on a
// tick revalidation — both opposite to fetchPlan).
//
// Two layers, matching the repo's pattern (fetchPlan.ts / activation.ts):
//   • createTriggerCore — pure, React-free. Owns prevNonce/prevTick/hasContent
//     and the fetch generation. Unit-tested exhaustively (fetch-trigger.test.ts).
//   • useFetchTrigger — the thin hook. Owns the reload nonce and the
//     activation+mutation tick subscription, and hands the core the current
//     values.
//
// Usage inside a hook's fetch effect:
//   const trigger = useFetchTrigger();
//   useEffect(() => {
//     const run = trigger.begin();
//     api(path, run.init).then((body) => {
//       if (!run.isCurrent()) return; // superseded / cancelled
//       run.markContent();
//       ...
//     });
//     return run.cancel;
//   }, [...deps, trigger.nonce, trigger.tick]);

// Everything one fetch needs to know about itself.
export interface TriggerRun {
  // True iff this fetch was caused by reload() — a post-write refetch. Bypasses
  // the cache ONCE; the next plain dep change reads cacheable again.
  isReload: boolean;
  // True iff this fetch was caused by a tick bump — an on-return activation
  // revalidation or a mutation-bus signal. Bypasses the cache and, with content
  // on screen, is silent (no loading flash).
  isRevalidate: boolean;
  // Whether content was on screen when this fetch began (drives silent-vs-
  // loading, and the "hold last-known value on a failed revalidation" rule).
  hasContent: boolean;
  // The shared fetchPlan decision for this trigger.
  plan: FetchPlan;
  // The plan as an api() options fragment: { cache: 'reload' } when bypassing,
  // {} otherwise — the exact shape every call site used to build by hand.
  init: { cache?: RequestCache };
  // Generation guard: true while no newer begin() has run and the effect that
  // started this fetch has not been cleaned up. Check before applying a result.
  isCurrent(): boolean;
  // Effect cleanup — marks this run stale (unmount, or a dep change re-running
  // the effect).
  cancel(): void;
  // Record that content is now on screen; feeds the NEXT run's hasContent.
  markContent(): void;
}

export interface TriggerCore {
  begin(nonce: number, tick: number, o?: { hasContent?: boolean }): TriggerRun;
}

export interface TriggerCoreOpts {
  // Start with content already on screen (useResource's optimistic seed) — the
  // first fetch hydrates silently instead of showing a skeleton.
  seeded?: boolean;
  // The tick value at mount, so the FIRST fetch is never mistaken for a
  // revalidation (a hook mounting after earlier writes elsewhere sees a
  // non-zero mutation version — that is its baseline, not a change).
  initialTick?: number;
}

// The pure heart. Exported for unit tests; hooks reach it via useFetchTrigger.
export function createTriggerCore(opts: TriggerCoreOpts = {}): TriggerCore {
  let prevNonce = 0;
  let prevTick = opts.initialTick ?? 0;
  let content = opts.seeded ?? false;
  let gen = 0;
  return {
    begin(nonce, tick, o) {
      const g = ++gen;
      let cancelled = false;
      const isReload = nonce !== prevNonce;
      const isRevalidate = tick !== prevTick;
      prevNonce = nonce;
      prevTick = tick;
      const hasContent = o?.hasContent ?? content;
      const plan = fetchPlan({ isReload, isRevalidate, hasContent });
      return {
        isReload,
        isRevalidate,
        hasContent,
        plan,
        init: plan.bypassCache ? { cache: 'reload' } : {},
        isCurrent: () => !cancelled && g === gen,
        cancel: () => {
          cancelled = true;
        },
        markContent: () => {
          content = true;
        },
      };
    },
  };
}

export interface UseFetchTriggerOpts {
  // Mutation tags (data domains) that revalidate this fetch. Default
  // (undefined) → the wildcard, i.e. any write. `[]` → never on writes.
  // See lib/mutationBus.ts.
  invalidatedBy?: string[];
  // false → ignore the activation and mutation ticks entirely (tick constant
  // 0). For a fetch mounted OUTSIDE the keep-alive shell (StaffView's
  // self-view), where there is no activation tick and write signals — e.g. the
  // sign-out POST — must not refetch. Default true.
  revalidate?: boolean;
  // Forwarded to the core: content is on screen from the first paint.
  seeded?: boolean;
}

export interface FetchTriggerHandle {
  // The reload nonce — put it in the fetch effect's deps.
  nonce: number;
  // The combined activation+mutation tick — put it in the fetch effect's deps.
  tick: number;
  // Bump the nonce: the next fetch is a cache-bypassing post-write reload.
  // Stable identity.
  reload(): void;
  // Call at the top of the fetch effect: resolves this run's trigger + plan and
  // supersedes the previous run. `hasContent` may be overridden per call for
  // hooks that derive it from their own state (useWindowedBookings).
  begin(o?: { hasContent?: boolean }): TriggerRun;
}

export function useFetchTrigger(opts: UseFetchTriggerOpts = {}): FetchTriggerHandle {
  const [nonce, setNonce] = useState(0);
  const activationTick = useActivationTick();
  const mutationTick = useMutationTick(opts.invalidatedBy);
  const tick = opts.revalidate === false ? 0 : activationTick + mutationTick;

  const coreRef = useRef<TriggerCore | null>(null);
  if (coreRef.current === null) {
    coreRef.current = createTriggerCore({ seeded: opts.seeded, initialTick: tick });
  }
  const core = coreRef.current;

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { nonce, tick, reload, begin: (o) => core.begin(nonce, tick, o) };
}

/**
 * SHOULD A PREFETCH BYPASS THE CACHE? Only on the run a reload or a tick
 * actually caused. The nonce and the tick are monotonic, so "either is
 * non-zero" is true for the hook's whole life after the first write or
 * activation, and every later window move re-fetched warm neighbours from the
 * Worker (codex, PR #176). Compare with what the last prefetch run saw.
 */
export function prefetchBypass(
  seen: { nonce: number; tick: number },
  now: { nonce: number; tick: number },
): boolean {
  return now.nonce !== seen.nonce || now.tick !== seen.tick;
}
